import { z } from "zod";

/**
 * Bike infrastructure lines: their shape on disk. The script bakes this file's
 * shape (`aube run bikeways`), and the map layer reads it — nothing else does,
 * so unlike districts there is no spatial index here.
 *
 * Two kinds, because they answer different safety questions on the ground:
 *   - `path` — physically separated from traffic: a cycle track, a greenway, a
 *     paved multi-use trail. You are not sharing a surface with cars.
 *   - `lane` — a painted lane on the roadway. Dedicated space, but paint only.
 *
 * Geometry is checked *shallowly*, same reasoning as `district.model.ts`: the
 * tag plus the presence of a coordinates array catches the realistic bake
 * failure, and walking ~60k positions per load re-proves nothing worth the cost.
 */

export type BikewayKind = z.infer<typeof BikewayKindSchema>;
export const BikewayKindSchema = z.enum(["path", "lane"]);

export type BikewayProps = z.infer<typeof BikewayPropsSchema>;
export const BikewayPropsSchema = z.object({
  kind: BikewayKindSchema,
});

/** O(1) per feature — see the note above on why the positions go unwalked. */
const LineStringSchema = z.custom<GeoJSON.LineString>(
  (value) =>
    typeof value === "object" &&
    value !== null &&
    (value as GeoJSON.LineString).type === "LineString" &&
    Array.isArray((value as GeoJSON.LineString).coordinates),
  { message: "expected a LineString geometry" },
);

export type BikewayFeature = z.infer<typeof BikewayFeatureSchema>;
export const BikewayFeatureSchema = z.object({
  type: z.literal("Feature"),
  properties: BikewayPropsSchema,
  geometry: LineStringSchema,
});

export type BikewayCollection = z.infer<typeof BikewayCollectionSchema>;
export const BikewayCollectionSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(BikewayFeatureSchema),
});
